import { useTranslations } from "adapters/i18n";
import type { CauseOfDeath } from "shared/types";
import type { CauseOfDeathFormState } from "../causeOfDeath.types";
import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import {
  CauseOfDeathNameError,
  validateCauseOfDeathName,
} from "../utils/causeOfDeath.validators";

type UseCauseOfDeathFormProps = {
  causesOfDeath: CauseOfDeath[];
  onSubmit: (formState: CauseOfDeathFormState) => void;
  selectedCauseOfDeath: CauseOfDeath | null;
};

export function useCauseOfDeathForm({
  causesOfDeath,
  onSubmit,
  selectedCauseOfDeath,
}: UseCauseOfDeathFormProps) {
  const { t } = useTranslations();

  const [formState, setFormState] = useState<CauseOfDeathFormState>({
    name: { value: selectedCauseOfDeath?.name ?? "", error: null },
  });

  const nameErrorToMessage: Record<CauseOfDeathNameError, string> = {
    [CauseOfDeathNameError.Required]: t("common:validation.required-field"),
    [CauseOfDeathNameError.Duplicated]: t(
      "livestock:duplicated-cause-of-death-name-error"
    ),
  };

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase();
    const { error } = validateCauseOfDeathName(
      value,
      causesOfDeath,
      selectedCauseOfDeath?.id ?? null
    );
    const errorMessage = error != null ? nameErrorToMessage[error] : null;

    setFormState((prevState) => ({
      ...prevState,
      name: { value, error: errorMessage },
    }));
  };

  const handleResetForm = () => {
    setFormState({
      name: { value: "", error: null },
    });
  };

  const canSubmitForm = useMemo(() => {
    const { isValid: isNameValid } = validateCauseOfDeathName(
      formState.name.value,
      causesOfDeath,
      selectedCauseOfDeath?.id ?? null
    );

    return isNameValid;
  }, [causesOfDeath, formState.name.value, selectedCauseOfDeath?.id]);

  const handleSubmitForm = useCallback(async () => {
    if (!canSubmitForm) {
      return null;
    }

    onSubmit(formState);
  }, [canSubmitForm, formState, onSubmit]);

  return {
    canSubmitForm,
    formState,
    handleChangeName,
    handleResetForm,
    handleSubmitForm,
  };
}
